import useVuelidate from "@vuelidate/core";
import { helpers, required } from "@vuelidate/validators";
import { storeToRefs } from "pinia";
import PQueue from "p-queue";
import { debounce } from "perfect-debounce";
import {
  ActivitiesResponseQuizSubTypeEnum,
  ActivityListItemTypeEnum,
  type ActivitiesResponse,
} from "@simbelapp/order-sdk";
import { useActivitiesStore } from "~/store/activities/activities.store";
import { useCreateBuilderTrainingStore } from "~/store/trainings/create-training-builder.store";
import { DebounceEnum } from "~/utils/enums/debounce.enums";

/**
 * The LMS builder saves the open activité as the author types: the fields touched within 200 ms go out in one save, one
 * save at a time, while the builder shows « Sauvegarde en cours ». An edit is saved on the activité it was made on: the
 * author can open another one before that save goes out.
 */
export const useActivityAutosave = () => {
  const activitiesStore = useActivitiesStore();
  const builderStore = useCreateBuilderTrainingStore();
  const queue = new PQueue({ concurrency: 1 });
  queue.on("active", () => (builderStore.isLoading = true));
  queue.on("idle", () => (builderStore.isLoading = false));

  // By edited activité, its fields and the edits waiting for the save that carries them. flush can't return that save:
  // perfect-debounce drops the calls made while the promise flush returned is pending.
  const activityEdits = new Map<
    ActivitiesResponse,
    { fields: Set<string>; waiting: ((stored: Promise<ActivitiesResponse | null>) => void)[] }
  >();
  const quizEdits = new Map<string, { activity: ActivitiesResponse; fields: Set<string> }>();

  const flush = debounce(
    () => {
      const trainingId = builderStore.training.content.trainingId;
      for (const [activity, { fields, waiting }] of activityEdits) {
        const list = [...fields];
        const stored =
          trainingId && list.length
            ? queue
                .add(() => activitiesStore.updateActivity(activity, list, trainingId))
                .then(
                  (saved) => saved ?? null,
                  () => null,
                )
            : Promise.resolve(null);
        waiting.forEach((resolve) => resolve(stored));
      }
      activityEdits.clear();
      for (const [quizId, { activity, fields }] of quizEdits) {
        const list = [...fields];
        queue.add(() => activitiesStore.updateActivityQuiz(activity, quizId, list));
      }
      quizEdits.clear();
    },
    DebounceEnum.DEFAULT,
    { trailing: false },
  );

  return {
    // Resolves with the activité as orders stored it with these fields, or null when that save failed.
    saveActivity(fields: string[]) {
      const activity = activitiesStore.currentActivity;
      if (!activity) return Promise.resolve(null);
      const edits = activityEdits.get(activity) ?? { fields: new Set<string>(), waiting: [] };
      fields.forEach((field) => edits.fields.add(field));
      activityEdits.set(activity, edits);
      const stored = new Promise<ActivitiesResponse | null>((resolve) => edits.waiting.push(resolve));
      flush();
      return stored;
    },
    saveQuiz(quizId: string, fields: string[]) {
      const activity = activitiesStore.currentActivity;
      if (!activity) return;
      const edits = quizEdits.get(quizId) ?? { activity, fields: new Set<string>() };
      fields.forEach((field) => edits.fields.add(field));
      quizEdits.set(quizId, edits);
      flush();
    },
  };
};

export const useActivitiesValidation = () => {
  function validateActivity() {
    const activitiesStore = useActivitiesStore();
    const { activities } = storeToRefs(activitiesStore);

    const rules = computed(() => {
      return {
        type: {
          required: helpers.withMessage("Ce champ est obligatoire", required),
        },
      };
    });

    activities.value.map(async (activity) => {
      const si$ = useVuelidate(rules, activity);
      await si$.value.$validate();
      activitiesStore.pushValidationErrors(si$.value.$errors, activity.activityId);
    });
  }

  function validateEmptyActivityQuiz() {
    const activitiesStore = useActivitiesStore();
    const { activities } = storeToRefs(activitiesStore);

    const rules = computed(() => {
      return {
        quizType: {
          required: helpers.withMessage("Ce champ est obligatoire", required),
        },
      };
    });

    activities.value.map(async (activity) => {
      if (activity.type !== ActivityListItemTypeEnum.Quiz) return;
      const si$ = useVuelidate(rules, { quizType: activity.quizType });
      await si$.value.$validate();
      activitiesStore.pushValidationErrors(si$.value.$errors, activity.activityId);
    });
  }

  function validateActivityQuiz(quizId: string) {
    const activitiesStore = useActivitiesStore();
    const { currentActivity, quizzesErrors } = storeToRefs(activitiesStore);

    const hasRightAnswer = (value: any) => {
      return value.find((answer: any) => answer.isRightAnswer === true);
    };

    const rules = computed(() => {
      return {
        quizAnswers: {
          hasRightAnswer: helpers.withMessage("Au moins une réponse doit être correcte", hasRightAnswer),
        },
      };
    });

    // Remove from errors the quizId
    quizzesErrors.value = quizzesErrors.value.filter((error) => error?.quizId !== quizId);

    const quizDetail = currentActivity.value?.quiz;
    const activityId = currentActivity.value?.activityId;
    if (
      quizDetail?.subType !== ActivitiesResponseQuizSubTypeEnum.MultipleChoiceQuestion ||
      quizDetail.quizId !== quizId ||
      !activityId
    ) {
      return;
    }
    const quizAnswersRef = ref(quizDetail.answers);
    const si$ = useVuelidate(rules, { quizAnswers: quizAnswersRef });
    si$.value
      .$validate()
      .then(() => activitiesStore.pushValidationQuizzesErrors(si$.value.$errors, activityId, quizDetail.quizId));
  }

  return {
    validateActivity,
    validateActivityQuiz,
    validateEmptyActivityQuiz,
  };
};
